"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  ListOrdered,
  Crosshair,
  Layers,
  Settings,
  RefreshCw,
  Upload,
  Wrench,
  UserCog,
  Coins,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";
import { useCharState } from "@/lib/state";
import { useAdminVisible } from "@/lib/admin";
import { CLASS_BY_ID } from "@/lib/classes";

interface NavItem {
  href: string;
  label: string;
  Icon: typeof LayoutDashboard;
  hint?: string;
}

const PRIMARY: NavItem[] = [
  { href: "/dashboard",       label: "Dashboard",       Icon: LayoutDashboard },
  { href: "/recommendations", label: "Recommendations", Icon: ListOrdered, hint: "Where to spend rolls" },
  { href: "/bis",             label: "BiS",             Icon: Crosshair, hint: "Wishlist per slot" },
  { href: "/pools",           label: "Pools",           Icon: Layers, hint: "Mark already-rolled items" },
];

const SECONDARY: NavItem[] = [
  { href: "/settings", label: "Settings", Icon: Settings },
];

const ADMIN: NavItem[] = [
  { href: "/admin/upload", label: "Upload cache", Icon: Upload,  hint: "Paste /tbr export" },
  { href: "/admin/items",  label: "Edit items",   Icon: Wrench,  hint: "Fix eligibleSpecs by hand" },
  { href: "/admin/specs",  label: "Edit by spec", Icon: UserCog, hint: "Trim each spec's loot" },
  { href: "/admin/tokens", label: "Tier tokens",  Icon: Coins,   hint: "Add tokens per boss" },
];

export function Sidebar() {
  const pathname = usePathname();
  const state = useCharState();
  const cls = CLASS_BY_ID[state.classID];
  const admin = useAdminVisible();

  return (
    <aside
      className={cn(
        "sticky top-0 h-screen shrink-0 border-r border-border bg-surface/60",
        "w-[64px] lg:w-[240px] transition-[width] flex flex-col z-30",
      )}
    >
      <div className="h-14 flex items-center border-b border-border px-4">
        <Link href="/" className="block">
          <span className="hidden lg:inline-flex"><Logo size="sm" /></span>
          <span className="lg:hidden inline-flex"><Logo size="sm" markOnly /></span>
        </Link>
      </div>

      <nav className="flex-1 px-2 py-4 flex flex-col gap-0.5 overflow-y-auto">
        <p className="eyebrow hidden lg:block px-3 mb-2">Workspace</p>
        {PRIMARY.map((item) => (
          <NavLink
            key={item.href}
            item={item}
            active={isActive(pathname, item.href)}
            classToken={cls?.token}
          />
        ))}

        <hr className="hairline my-4" />

        <p className="eyebrow hidden lg:block px-3 mb-2">Account</p>
        {SECONDARY.map((item) => (
          <NavLink
            key={item.href}
            item={item}
            active={isActive(pathname, item.href)}
            classToken={cls?.token}
          />
        ))}

        {admin && (
          <>
            <hr className="hairline my-4" />
            <p className="eyebrow hidden lg:block px-3 mb-2">Admin</p>
            {ADMIN.map((item) => (
              <NavLink
                key={item.href}
                item={item}
                active={isActive(pathname, item.href)}
                classToken={cls?.token}
              />
            ))}
          </>
        )}
      </nav>

      <div className="border-t border-border p-2">
        <Link
          href="/"
          className={cn(
            "w-full inline-flex items-center gap-3 rounded-sm px-3 h-10",
            "text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors text-sm",
          )}
        >
          <RefreshCw className="h-4 w-4 shrink-0" />
          <span className="hidden lg:inline">Change class</span>
        </Link>
      </div>
    </aside>
  );
}

function NavLink({
  item,
  active,
  classToken,
}: {
  item: NavItem;
  active: boolean;
  classToken?: string;
}) {
  const accent = active && classToken
    ? { borderLeftColor: `var(--color-class-${classToken})` }
    : undefined;
  return (
    <Link
      href={item.href}
      title={item.hint}
      className={cn(
        "group relative inline-flex items-center gap-3 h-10 px-3 rounded-sm",
        "text-sm transition-colors border-l-2",
        active
          ? "bg-surface-2 text-fg border-l-accent"
          : "text-fg-muted border-l-transparent hover:text-fg hover:bg-surface-2/50",
      )}
      style={accent}
    >
      <item.Icon
        className={cn(
          "h-4 w-4 shrink-0 transition-colors",
          active ? "text-accent" : "text-fg-subtle group-hover:text-fg-muted",
        )}
      />
      <span className="hidden lg:inline">{item.label}</span>
    </Link>
  );
}

function isActive(pathname: string | null, href: string): boolean {
  if (!pathname) return false;
  if (pathname === href) return true;
  return pathname.startsWith(href + "/");
}
